import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faSpotify, faInstagram, faAmazon } from '@fortawesome/free-brands-svg-icons';

const SOCIAL = [
  { href: "https://www.youtube.com/@elayonemusic",                           icon: faYoutube,   label: "YouTube" },
  { href: "https://open.spotify.com/artist/2bSTqIGNwOAu8ETmKSVtnf",         icon: faSpotify,   label: "Spotify" },
  { href: "https://www.instagram.com/elayonemusic?igsh=N3ZrcGJwNzByYzB6",   icon: faInstagram, label: "Instagram" },
  { href: "https://music.amazon.com/artists/B081CZ6K9B/elayone-music",       icon: faAmazon,    label: "Amazon" },
];

// Uses the same brand CSS tokens (--paper/--ink/--accent/--wine…) as the
// event hero, so the footer follows light/dark mode and stays on-brand.
const Footer = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/api/subscription/sub`, formData);
      setMessage(res.data.message);
      setIsError(false);
      setIsSubscribed(true);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Subscription failed. Please try again.');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/api/subscription/verify`, {
        email: formData.email,
        code: verificationCode,
      });
      setMessage(res.data.message);
      setIsError(false);
      setFormData({ firstName: '', lastName: '', email: '' });
      setVerificationCode('');
      setIsSubscribed(false);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Verification failed. Please try again.');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[var(--paper)] text-[var(--ink)] w-full border-t border-[var(--border)] pt-12 transition-colors duration-300">

      {/* ── Top section ── */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left — Brand + Subscribe */}
          <div className="space-y-10">
            {/* Brand */}
            <div>
              <h2 className="font-ettlora text-3xl md:text-4xl text-[var(--ink)] mb-2">Elayone Music</h2>
              <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--ink-soft)]">
                Music that moves the soul
              </p>
            </div>

            {/* Subscribe heading */}
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-soft)] mb-6">
                Stay Connected
              </p>

              {!isSubscribed ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text" name="firstName" placeholder="First Name"
                      value={formData.firstName} onChange={handleChange}
                      className="bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)]
                        text-[var(--ink)] text-[13px] placeholder-[var(--ink-soft)] py-2.5 outline-none
                        transition-colors duration-200 w-full"
                    />
                    <input
                      type="text" name="lastName" placeholder="Last Name"
                      value={formData.lastName} onChange={handleChange}
                      className="bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)]
                        text-[var(--ink)] text-[13px] placeholder-[var(--ink-soft)] py-2.5 outline-none
                        transition-colors duration-200 w-full"
                    />
                  </div>
                  <div className="flex gap-3">
                    <input
                      type="email" name="email" placeholder="Email address"
                      value={formData.email} onChange={handleChange}
                      className="flex-1 bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)]
                        text-[var(--ink)] text-[13px] placeholder-[var(--ink-soft)] py-2.5 outline-none
                        transition-colors duration-200"
                    />
                    <button
                      onClick={handleSubmit} disabled={loading}
                      className="text-[11px] tracking-[0.2em] uppercase text-[var(--on-wine)] bg-[var(--wine)]
                        px-5 py-2.5 hover:bg-[var(--wine-deep)] transition-colors duration-200
                        disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {loading ? '...' : 'Subscribe'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[12px] text-[var(--ink-soft)]">Enter the verification code sent to your email.</p>
                  <div className="flex gap-3">
                    <input
                      type="text" placeholder="Verification code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="flex-1 bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)]
                        text-[var(--ink)] text-[13px] placeholder-[var(--ink-soft)] py-2.5 outline-none
                        transition-colors duration-200"
                    />
                    <button
                      onClick={handleVerify} disabled={loading}
                      className="text-[11px] tracking-[0.2em] uppercase text-[var(--on-wine)] bg-[var(--wine)]
                        px-5 py-2.5 hover:bg-[var(--wine-deep)] transition-colors duration-200
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? '...' : 'Verify'}
                    </button>
                  </div>
                </div>
              )}

              {message && (
                <p className={`text-[12px] mt-3 ${isError ? 'text-red-500' : 'text-[var(--ink-soft)]'}`}>
                  {message}
                </p>
              )}
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-5">
              {SOCIAL.map(({ href, icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  aria-label={label}
                  className="text-[var(--ink-soft)] hover:text-[var(--accent)] transition-all duration-200 hover:scale-110 text-lg">
                  <FontAwesomeIcon icon={icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Right — Map */}
          <div className="space-y-3">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-soft)] mb-4">Find Us</p>
            <div className="overflow-hidden border border-[var(--border)]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d996.8647636425184!2d30.096268666074966!3d-1.9704748980116846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19dca64422b0994f%3A0x120ecbd09a3ed830!2s8%20KK%20500%20St%2C%20Kigali!5e0!3m2!1sen!2srw!4v1744717182670!5m2!1sen!2srw"
                width="100%" height="280"
                style={{ border: 0, filter: 'grayscale(10%) contrast(1) brightness(0.95)' }}
                allowFullScreen="" loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Elayone Music Location"
              />
            </div>
            <p className="text-[11px] text-[var(--ink-soft)] tracking-wide">
              KK 500 St, House No 8, Zinia, Kicikiro, Kigali, Rwanda
            </p>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-[var(--border)]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-5
          flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-6">
            <a href="/about"
              className="text-[11px] tracking-[0.2em] uppercase text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors duration-200">
              About
            </a>
            <a href="/donation"
              className="text-[11px] tracking-[0.2em] uppercase text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors duration-200">
              Donate
            </a>
          </div>
          <p className="text-[11px] text-[var(--ink-soft)] tracking-wide text-center">
            © {new Date().getFullYear()} Elayone Music. All rights reserved.
          </p>
          <p className="text-[11px] text-[var(--ink-soft)]">
            Developed By{' '}
            <a href="https://www.enoveta.com" target="_blank" rel="noopener noreferrer"
              className="hover:text-[var(--accent)] transition-colors duration-200">
              Enoveta Inc
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
